import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { usePageTitle } from '../../../hooks/usePageTitle';
import { useUnviewedLeadsCount } from '../../../hooks/useUnviewedLeads';
import { useRealtime } from '../../../contexts/RealtimeContext';
import { manualDrawApi } from '../../../services/api';

// Components
import LeadsPageHeader from './components/LeadsPageHeader';
import StatsCards from './components/StatsCards';
import LeadsFilters from './components/LeadsFilters';
import LeadsTable from './components/LeadsTable';
import LeadDetailsModal from './components/LeadDetailsModal';
import CSVImportModal from '../ManualDrawPage/components/CSVImportModal';
import { LoadingState } from '../../../components/LoadingSpinner';

// Hooks
import { useLeadsData, Lead } from './hooks/useLeadsData';

// Utils
import { formatBirthdate } from './utils/dateUtils';

const LeadsPage: React.FC = () => {
  usePageTitle('Leads');
  const [searchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  // Hook para marcar leads como visualizados
  const { markAsViewed } = useUnviewedLeadsCount();
  const { lastEvent } = useRealtime();

  const {
    leads,
    wheels,
    loading,
    filters,
    pagination,
    stats,
    loadWheels,
    loadLeads,
    updateFilter,
    clearFilters
  } = useLeadsData();

  // Ler parâmetro filter da URL
  useEffect(() => {
    const filter = searchParams.get('filter');
    if (filter === 'birthdays') {
      updateFilter('is_birthday', true);
      setShowFilters(true);
    }
  }, [searchParams, updateFilter]);

  useEffect(() => {
    loadWheels();
  }, [loadWheels]);

  useEffect(() => {
    loadLeads();
  }, [filters, loadLeads]);

  useEffect(() => {
    if (wheels.length) {
      loadLeads();
    }
  }, [wheels, loadLeads]);

  // Novo cliente chegou em tempo real: recarregar a lista
  const lastEventRef = useRef(lastEvent);
  useEffect(() => {
    if (lastEvent && lastEvent !== lastEventRef.current) {
      lastEventRef.current = lastEvent;
      loadLeads();
    }
  }, [lastEvent, loadLeads]);

  // Marcar leads como visualizados quando a página carregar
  useEffect(() => {
    if (leads.length > 0) {
      const leadIds = leads.map(lead => lead.id);
      // Marcar como visualizado após 2 segundos (para dar tempo do usuário ver)
      const timeout = setTimeout(() => {
        markAsViewed(leadIds);
      }, 2000);

      return () => clearTimeout(timeout);
    }
  }, [leads, markAsViewed]);

  const formatWhatsApp = (whatsapp: string) => {
    if (!whatsapp) return '';
    const numbers = whatsapp.replace(/\D/g, '');

    if (numbers.startsWith('55') && numbers.length >= 12) {
      const ddd = numbers.substring(2, 4);
      const firstPart = numbers.substring(4, 9);
      const secondPart = numbers.substring(9);
      return `+55 (${ddd}) ${firstPart}-${secondPart}`;
    }

    return whatsapp;
  };

  const openWhatsApp = (whatsapp: string) => {
    const cleanNumber = whatsapp.replace(/\D/g, '');
    const url = `https://wa.me/${cleanNumber}`;
    window.open(url, '_blank');
  };

  const handleRowClick = (lead: Lead) => {
    setSelectedLead(lead);
    setIsDetailsModalOpen(true);
  };

  const closeDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedLead(null);
  };

  const handleImportSuccess = () => {
    loadLeads(); // Recarregar leads após importação
    toast.success('Leads importados com sucesso!');
  };

  const handleDeleteLead = async (leadId: number) => {
    try {
      await manualDrawApi.deleteLead(leadId);
      toast.success('Lead excluído com sucesso!');
      loadLeads(); // Recarregar lista
    } catch (error: any) {
      toast.error(error.message || 'Erro ao excluir lead');
    }
  };

  const exportToCSV = () => {
    if (!leads.length) {
      toast.error('Nenhum lead para exportar');
      return;
    }

    // Coletar todos os campos customizados únicos e mapear para labels
    const customFieldsMap = new Map<string, string>();

    leads.forEach(lead => {
      if (lead.custom_data && lead.custom_fields) {
        Object.keys(lead.custom_data).forEach(fieldId => {
          if (!customFieldsMap.has(fieldId)) {
            const field = lead.custom_fields?.find((f: any) => f.id === fieldId);
            const label = field?.label || fieldId;
            customFieldsMap.set(fieldId, label);
          }
        });
      }
    });

    const customFieldIds = Array.from(customFieldsMap.keys());
    const customFieldLabels = customFieldIds.map(id => customFieldsMap.get(id) || id);

    const headers = [
      'Nome',
      'WhatsApp',
      'Data de Nascimento',
      'Sorteio',
      'Produto Ganho',
      'Ganhou?',
      ...customFieldLabels,
      'Data/Hora'
    ];

    const csvData = leads.map(lead => [
      lead.customer_name,
      lead.whatsapp,
      lead.customer_birthdate ? formatBirthdate(lead.customer_birthdate) : '',
      lead.wheel_name,
      // Múltiplos produtos separados por ponto e vírgula
      lead.product_ids && lead.product_ids.length > 1
        ? (lead.product_names?.join('; ') || `${lead.product_ids.length} prêmios`)
        : (lead.product_name || 'Nenhum'),
      lead.won ? 'Sim' : 'Não',
      ...customFieldIds.map(fieldId => lead.custom_data?.[fieldId] || ''),
      new Date(lead.created_at).toLocaleString('pt-BR')
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `leads-${Date.now()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Lista de leads exportada com sucesso!');
  };

  if (loading && !wheels.length) {
    return <LoadingState message="Carregando clientes..." />;
  }

  return (
    <div className="space-y-6">
      <LeadsPageHeader
        onToggleFilters={() => setShowFilters(!showFilters)}
        onExportCSV={exportToCSV}
        onImportCSV={() => setShowImportModal(true)}
        hasLeads={leads.length > 0}
        filtersOpen={showFilters}
      />

      <StatsCards stats={stats} />

      {showFilters && (
        <LeadsFilters
          filters={filters}
          wheels={wheels}
          onFilterChange={updateFilter}
          onClearFilters={clearFilters}
        />
      )}

      <LeadsTable
        leads={leads}
        loading={loading}
        filters={filters}
        pagination={pagination}
        onRowClick={handleRowClick}
        onOpenWhatsApp={openWhatsApp}
        formatWhatsApp={formatWhatsApp}
        onFilterChange={updateFilter}
      />

      <LeadDetailsModal
        isOpen={isDetailsModalOpen}
        lead={selectedLead}
        onClose={closeDetailsModal}
        onOpenWhatsApp={openWhatsApp}
        formatWhatsApp={formatWhatsApp}
        onDelete={handleDeleteLead}
      />

      <CSVImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportSuccess={handleImportSuccess}
      />
    </div>
  );
};

export default LeadsPage;
